/**
 * Single source of truth for supported app language codes and their metadata.
 * When adding a language: extend this object, mirror in `cloudFunctions/functions/src/languageRegistry.ts`,
 * then add entries in `senf-shared/locales/languages.json`.
 */

export const LANGUAGE_REGISTRY = {
  ar: {
    shortName: "AR",
    dir: "rtl" as const,
    /** Lowercase name for translation APIs / Cloud Functions. */
    translationName: "arabic",
    geminiEnglishLabel: "Arabic",
    icon: "locales/ar",
    ogLocale: "ar_SA",
  },
  cs: {
    shortName: "CS",
    dir: "ltr" as const,
    translationName: "czech",
    geminiEnglishLabel: "Czech",
    icon: "locales/cs",
    ogLocale: "cs_CZ",
  },
  de: {
    shortName: "DE",
    dir: "ltr" as const,
    translationName: "german",
    geminiEnglishLabel: "German",
    icon: "locales/de",
    ogLocale: "de_DE",
  },
  en: {
    shortName: "EN",
    dir: "ltr" as const,
    translationName: "english",
    geminiEnglishLabel: "English",
    icon: "locales/en",
    ogLocale: "en_US",
  },
  es: {
    shortName: "ES",
    dir: "ltr" as const,
    translationName: "spanish",
    geminiEnglishLabel: "Spanish (Spain)",
    icon: "locales/es",
    ogLocale: "es_ES",
  },
  fr: {
    shortName: "FR",
    dir: "ltr" as const,
    translationName: "french",
    geminiEnglishLabel: "French",
    icon: "locales/fr",
    ogLocale: "fr_FR",
  },
  it: {
    shortName: "IT",
    dir: "ltr" as const,
    translationName: "italian",
    geminiEnglishLabel: "Italian",
    icon: "locales/it",
    ogLocale: "it_IT",
  },
  latam: {
    shortName: "LATAM",
    dir: "ltr" as const,
    translationName: "mexican spanish",
    geminiEnglishLabel: "Spanish (Latin America)",
    icon: "locales/latam",
    ogLocale: "es_MX",
  },
  nl: {
    shortName: "NL",
    dir: "ltr" as const,
    translationName: "dutch",
    geminiEnglishLabel: "Dutch",
    icon: "locales/nl",
    ogLocale: "nl_NL",
  },
  pl: {
    shortName: "PL",
    dir: "ltr" as const,
    translationName: "polish",
    geminiEnglishLabel: "Polish",
    icon: "locales/pl",
    ogLocale: "pl_PL",
  },
  ru: {
    shortName: "RU",
    dir: "ltr" as const,
    translationName: "russian",
    geminiEnglishLabel: "Russian",
    icon: "locales/ru",
    ogLocale: "ru_RU",
  },
  tr: {
    shortName: "TR",
    dir: "ltr" as const,
    translationName: "turkish",
    geminiEnglishLabel: "Turkish",
    icon: "locales/tr",
    ogLocale: "tr_TR",
  },
  uk: {
    shortName: "UK",
    dir: "ltr" as const,
    translationName: "ukrainian",
    geminiEnglishLabel: "Ukrainian",
    icon: "locales/uk",
    ogLocale: "uk_UA",
  },
} as const;

export type Lang = keyof typeof LANGUAGE_REGISTRY;

export const localeToOgLocale = Object.fromEntries(
  (Object.keys(LANGUAGE_REGISTRY) as Lang[]).map((k) => [
    k,
    LANGUAGE_REGISTRY[k].ogLocale,
  ]),
) as Record<Lang, string>;
